import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  pjNfMensalService,
  type PjNfCompetenciaSummary,
  type PjNfMensalRow,
  type PjNfMensalStatus,
} from '@/services/pjNfMensalService'
import { currentCompetencia } from '@/components/PjNf/pjNfStatus'
import { getFriendlyError } from '@/utils/getFriendlyError'

export function useFinancePagamentosPage() {
  const [competencia, setCompetencia] = useState(currentCompetencia)
  const [rows, setRows] = useState<PjNfMensalRow[]>([])
  const [summary, setSummary] = useState<PjNfCompetenciaSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<PjNfMensalStatus | ''>('')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selected, setSelected] = useState<PjNfMensalRow | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [list, sum] = await Promise.all([
        pjNfMensalService.listCompetencia(competencia, {
          status: statusFilter || undefined,
          search: search.trim() || undefined,
        }),
        pjNfMensalService.getCompetenciaSummary(competencia),
      ])
      setRows(list)
      setSummary(sum)
    } catch (err) {
      setError(getFriendlyError(err, 'Erro ao carregar pagamentos PJ'))
    } finally {
      setLoading(false)
    }
  }, [competencia, statusFilter, search])

  useEffect(() => {
    void load()
  }, [load])

  const openDetail = useCallback(async (row: PjNfMensalRow) => {
    setSelectedId(row.id)
    setSelected(row)
    setDetailLoading(true)
    try {
      const detail = await pjNfMensalService.getById(row.id)
      setSelected(detail)
    } catch (err) {
      toast.error(getFriendlyError(err, 'Erro ao abrir NF'))
    } finally {
      setDetailLoading(false)
    }
  }, [])

  const closeDetail = useCallback(() => {
    setSelectedId(null)
    setSelected(null)
  }, [])

  const marcarEmValidacao = useCallback(
    async (id: string) => {
      try {
        await pjNfMensalService.marcarEmValidacao(id)
        toast.success('NF em validação')
        closeDetail()
        await load()
      } catch (err) {
        toast.error(getFriendlyError(err, 'Erro ao atualizar status'))
        return false
      }
      return true
    },
    [closeDetail, load],
  )

  const validar = useCallback(
    async (id: string, aprovada: boolean, rejeicaoMotivo?: string) => {
      try {
        await pjNfMensalService.validar(id, { aprovada, rejeicaoMotivo })
        toast.success(aprovada ? 'NF aprovada' : 'NF rejeitada')
        closeDetail()
        await load()
      } catch (err) {
        toast.error(getFriendlyError(err, 'Erro ao validar NF'))
        return false
      }
      return true
    },
    [closeDetail, load],
  )

  const pagar = useCallback(
    async (id: string, observacoes?: string) => {
      try {
        await pjNfMensalService.pagar(id, { observacoes })
        toast.success('Pagamento registrado')
        closeDetail()
        await load()
      } catch (err) {
        toast.error(getFriendlyError(err, 'Erro ao registrar pagamento'))
        return false
      }
      return true
    },
    [closeDetail, load],
  )

  return {
    competencia,
    setCompetencia,
    rows,
    summary,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    reload: load,
    selectedId,
    selected,
    detailLoading,
    openDetail,
    closeDetail,
    marcarEmValidacao,
    validar,
    pagar,
  }
}
